import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Lightning, GoogleLogo, ArrowLeft, ShieldCheck } from '@phosphor-icons/react';

function handleGoogleLogin() {
  var redirectUrl = window.location.origin + '/';
  window.location.href = 'https://auth.emergentagent.com/?redirect=' + encodeURIComponent(redirectUrl);
}

export default function LoginPage() {
  const { user, login, setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('login'); // login | forgot | reset | 2fa
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');

  useEffect(() => {
    if (user) {
      const dest = user.role === 'admin' ? '/admin' : user.role === 'advertiser' ? '/advertiser' : '/affiliate';
      navigate(dest);
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/auth/login', { email, password, totp_code: totpCode });
      if (data.requires_2fa) {
        setView('2fa');
        setLoading(false);
        return;
      }
      if (data.token) localStorage.setItem('dw_token', data.token);
      setUser(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    }
    setLoading(false);
  };

  const handle2faSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/auth/login', { email, password, totp_code: totpCode });
      if (data.token) localStorage.setItem('dw_token', data.token);
      setUser(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid 2FA code');
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) { setError('Enter your email address'); return; }
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email });
      toast.success('Reset code sent! Check your email.');
      setView('reset');
    } catch (err) {
      toast.success('If an account exists, a reset code has been sent.');
      setView('reset');
    }
    setLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    if (newPassword.length < 6) { setError('Password must be at least 6 characters'); return; }
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/auth/reset-password', { email, code: resetCode, new_password: newPassword });
      if (data.token) localStorage.setItem('dw_token', data.token);
      toast.success('Password reset! Logging you in...');
      window.location.href = '/dashboard';
      return;
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail) {
        setError(detail);
      } else {
        setError('Reset failed. Please request a new code and try again.');
      }
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Lightning size={28} weight="duotone" className="text-white" />
          </div>
          <h1 className="font-heading text-3xl font-bold text-white">Dynamic World</h1>
          <p className="text-sm text-zinc-400 mt-1">Affiliate Operating System</p>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">

          {/* ===== LOGIN VIEW ===== */}
          {view === 'login' && (
            <>
              <h2 className="font-heading text-xl font-semibold text-white mb-6">Sign In</h2>
              {error && <div data-testid="login-error" className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">{error}</div>}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-zinc-300 text-sm">Email</Label>
                  <Input data-testid="login-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100 placeholder-zinc-500" />
                </div>
                <div>
                  <Label className="text-zinc-300 text-sm">Password</Label>
                  <Input data-testid="login-password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter your password" required className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100 placeholder-zinc-500" />
                </div>
                <div className="flex justify-end">
                  <button type="button" onClick={() => setView('forgot')} data-testid="forgot-password-link" className="text-xs text-blue-400 hover:text-blue-300 transition-colors">
                    Forgot password?
                  </button>
                </div>
                <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium">
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>
              <div className="relative my-5">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-zinc-800" /></div>
                <div className="relative flex justify-center text-xs"><span className="bg-zinc-900 px-3 text-zinc-500">or continue with</span></div>
              </div>
              <Button data-testid="google-login-btn" type="button" onClick={handleGoogleLogin} className="w-full bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border border-zinc-700 gap-2">
                <GoogleLogo size={18} weight="bold" /> Sign in with Google
              </Button>
              <p className="text-sm text-zinc-400 text-center mt-6">
                Don't have an account?{' '}
                <Link to="/register" className="text-blue-400 hover:text-blue-300" data-testid="go-to-register">Create Account</Link>
              </p>
            </>
          )}

          {/* ===== 2FA VIEW ===== */}
          {view === '2fa' && (
            <>
              <button onClick={() => { setView('login'); setTotpCode(''); setError(''); }} className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-300 mb-4"><ArrowLeft size={14} /> Back to login</button>
              <div className="flex items-center gap-2 mb-4">
                <ShieldCheck size={22} className="text-blue-400" />
                <h2 className="font-heading text-xl font-semibold text-white">Two-Factor Authentication</h2>
              </div>
              <p className="text-sm text-zinc-400 mb-4">Enter the 6-digit code from your authenticator app.</p>
              {error && <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">{error}</div>}
              <form onSubmit={handle2faSubmit} className="space-y-4">
                <Input data-testid="2fa-code" type="text" value={totpCode} onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" maxLength={6}
                  className="text-center text-2xl font-mono tracking-[0.5em] bg-zinc-950 border-zinc-800 text-zinc-100 h-14" autoFocus />
                <Button type="submit" disabled={loading || totpCode.length < 6} data-testid="2fa-submit" className="w-full bg-blue-600 hover:bg-blue-700">
                  {loading ? 'Verifying...' : 'Verify & Sign In'}
                </Button>
              </form>
            </>
          )}

          {/* ===== FORGOT PASSWORD VIEW ===== */}
          {view === 'forgot' && (
            <>
              <button onClick={() => { setView('login'); setError(''); }} className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-300 mb-4"><ArrowLeft size={14} /> Back to login</button>
              <h2 className="font-heading text-xl font-semibold text-white mb-2">Forgot Password</h2>
              <p className="text-sm text-zinc-400 mb-4">Enter your email and we'll send a reset code.</p>
              {error && <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">{error}</div>}
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <Label className="text-zinc-300 text-sm">Email</Label>
                  <Input data-testid="forgot-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100 placeholder-zinc-500" />
                </div>
                <Button type="submit" disabled={loading} data-testid="send-reset-btn" className="w-full bg-blue-600 hover:bg-blue-700">
                  {loading ? 'Sending...' : 'Send Reset Code'}
                </Button>
              </form>
            </>
          )}

          {/* ===== RESET PASSWORD VIEW ===== */}
          {view === 'reset' && (
            <>
              <button onClick={() => { setView('forgot'); setError(''); }} className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-300 mb-4"><ArrowLeft size={14} /> Back</button>
              <h2 className="font-heading text-xl font-semibold text-white mb-2">Reset Password</h2>
              <p className="text-sm text-zinc-400 mb-4">Enter the code sent to <strong className="text-zinc-200">{email}</strong> and your new password.</p>
              {error && <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">{error}</div>}
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <Label className="text-zinc-300 text-sm">Reset Code</Label>
                  <Input data-testid="reset-code" type="text" value={resetCode} onChange={e => setResetCode(e.target.value.trim())} placeholder="6-digit code" required className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100 placeholder-zinc-500 font-mono tracking-[0.3em] text-center text-xl" maxLength={6} />
                </div>
                <div>
                  <Label className="text-zinc-300 text-sm">New Password</Label>
                  <Input data-testid="reset-new-password" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="At least 6 characters" required minLength={6} className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100 placeholder-zinc-500" />
                </div>
                <div>
                  <Label className="text-zinc-300 text-sm">Confirm Password</Label>
                  <Input data-testid="reset-confirm-password" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Re-enter new password" required className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100 placeholder-zinc-500" />
                  {confirmPassword && newPassword !== confirmPassword && (
                    <p className="text-xs text-red-400 mt-1">Passwords do not match</p>
                  )}
                </div>
                <Button type="submit" disabled={loading || newPassword !== confirmPassword} data-testid="reset-submit" className="w-full bg-blue-600 hover:bg-blue-700">
                  {loading ? 'Resetting...' : 'Reset Password & Sign In'}
                </Button>
              </form>
            </>
          )}
        </div>

        <p className="text-xs text-zinc-600 text-center mt-6">Dynamic World Ltd (United Kingdom)</p>
      </div>
    </div>
  );
}
